import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { ImageMeta } from '@app/entities/images/interfaces/image-meta';
import {
  AuthSelectors,
  CartSelectors,
  GallerySelectors,
  PopulateFilters,
  ResetGalleryFilters,
  RouterSelectors,
} from '@app/store';
import { Actions, ofActionDispatched, Store } from '@ngxs/store';
import { combineLatest, filter, map, Observable, tap } from 'rxjs';
import { SearchRequestComponent } from '../search-request/search-request.component';
import { CategoriesComponent } from './categories/categories.component';
import { MenuComponent } from './menu/menu.component';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    MenuComponent,
    AsyncPipe,
    SearchRequestComponent,
    NgTemplateOutlet,
    CategoriesComponent,
  ],
})
export class HeaderComponent implements OnInit {
  constructor(
    private destroyRef: DestroyRef,
    private router: Router,
    private actions$: Actions,
    private store: Store,
  ) {}

  @ViewChild(SearchRequestComponent)
  searchRequestComponent!: SearchRequestComponent;

  @ViewChild(MenuComponent, { read: ElementRef })
  menuComponent!: ElementRef<HTMLElement>;

  protected isAuthenticated$: Observable<boolean> = this.store.select(
    AuthSelectors.isAuthenticated,
  );

  protected currentUserAvatar$: Observable<ImageMeta | null | undefined> =
    this.store.select(AuthSelectors.currentUserAvatar);

  searchRequest$: Observable<string> = inject(Store).select(
    GallerySelectors.searchRequest,
  );

  isHomepage$: Observable<boolean> = inject(Store).select(
    RouterSelectors.isHomepage,
  );

  isGalleryPage$: Observable<boolean> = inject(Store).select(
    RouterSelectors.isGalleryPage,
  );

  hasItemsInCart$: Observable<boolean> = inject(Store).select(
    CartSelectors.hasItemsInCart,
  );

  displayCategories$!: Observable<boolean>;

  protected fc = new FormControl();

  protected displayMenu: boolean = false;

  ngOnInit(): void {
    this.handleNavigation();
    this.handlePopulateValue();
    this.handleResetValue();
    this.initDisplayCategories();
  }

  private initDisplayCategories(): void {
    this.displayCategories$ = combineLatest([
      this.isHomepage$,
      this.isGalleryPage$,
    ]).pipe(map(([isHomepage, isGalleryPage]) => isHomepage || isGalleryPage));
  }

  handlePopulateValue(): void {
    this.actions$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        ofActionDispatched(PopulateFilters),
        tap((action: PopulateFilters) => {
          this.fc.setValue(action.data.search || null);
        }),
      )
      .subscribe();
  }

  handleResetValue(): void {
    this.actions$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        ofActionDispatched(ResetGalleryFilters),
        tap(() => {
          this.fc.reset();
        }),
      )
      .subscribe();
  }

  private handleNavigation(): void {
    // Subscribe to router events to check for navigation to the /gallery route
    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((event) => event instanceof NavigationEnd),
        tap((event: NavigationEnd) => {
          // Check if the current route is '/gallery' (ignoring query parameters)
          const path = event.urlAfterRedirects.split('?')[0]; // Get path without query parameters
          if (path === '/gallery' || path === '/') {
            this.focus();
          }
        }),
      )
      .subscribe();
  }

  protected toggleDisplayMenu(e: MouseEvent): void {
    e.stopPropagation();
    this.displayMenu = !this.displayMenu;
  }

  protected onCloseMenu(): void {
    this.displayMenu = false;
  }

  @HostListener('document:click', ['$event'])
  onClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    // Check if the clicked target is outside the menu and the button
    if (
      this.displayMenu &&
      this.menuComponent &&
      !this.menuComponent.nativeElement.contains(target)
    ) {
      this.displayMenu = false;
    }
  }

  focus(): void {
    this.searchRequestComponent.focus();
  }
}
